import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScreenSize } from '@/hooks';
import { storage } from '@/utils';
import WelcomeBannerRightPanel from './WelcomeBannerRightPanel';
import styles from './WelcomeBanner.module.css';

const COINS = [
  { src: '/assets/images/coins/BTC.webp', alt: 'BTC' },
  { src: '/assets/images/coins/ETH.webp', alt: 'ETH' },
  { src: '/assets/images/coins/USDT.webp', alt: 'USDT' },
  { src: '/assets/images/coins/LTC.webp', alt: 'LTC' },
  { src: '/assets/images/coins/DOGE.webp', alt: 'DOGE' },
  { src: '/assets/images/coins/TRON.webp', alt: 'TRX' },
];

const PAYMENT_METHODS = [
  { name: 'Apple Pay', icon: '/assets/images/payment-method/apple-pay.svg' },
  { name: 'Google Pay', icon: '/assets/images/payment-method/google-pay.svg' },
  { name: 'Visa', icon: '/assets/images/payment-method/visa.svg' },
  { name: 'Mastercard', icon: '/assets/images/payment-method/master.svg' },
];

const duplicatedCoins = [...COINS, ...COINS];

const CoinSlider = ({ reverse }) => (
  <div
    className={`${styles.tickerRow} ${reverse ? styles.tickerRowRtl : ''}`}
    aria-hidden="true"
    style={{ pointerEvents: 'none', userSelect: 'none' }}
  >
    <div className={styles.tickerTrack}>
      {duplicatedCoins.map((coin, i) => (
        <div key={`${coin.alt}-${i}`} className={styles.tickerIconWrap}>
          <div className={styles.tickerIconInner}>
            <div className={styles.tickerIconImgWrap}>
              <img
                src={coin.src}
                alt={coin.alt}
                loading="eager"
                className={styles.tickerIconImg}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className={styles.tickerFadeLeft} aria-hidden="true" />
    <div className={styles.tickerFadeRight} aria-hidden="true" />
  </div>
);

// Welcome slide — diagonal stripes pattern
const WelcomeBgSvg = ({ clipId }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 376 170" preserveAspectRatio="xMidYMid slice">
    <g fill="currentColor" clipPath={`url(#${clipId})`}>
      <path d="M316.248.583c-32.457 56.328-32.457 112.69 0 169.018H375.5c-49.131-55.847-49.68-112.175 0-169.018z" />
      <path d="M158.014 169.635h59.492c-10.567-56.328-10.567-112.69 0-169.018h-59.492c10.533 57.427 10.67 111.626 0 169.018" />
      <path d="M296.487.583h-59.252c-10.945 56.328-10.945 112.69 0 169.018h59.252c-27.036-55.847-27.413-112.14 0-169.018m-236.769 0c32.457 56.328 32.457 112.69 0 169.018H.5C49.631 113.754 50.18 57.426.5.583h59.252z" />
      <path d="M79.48.583h59.252c10.945 56.328 10.945 112.69 0 169.018H79.48c27.036-55.847 27.413-112.14 0-169.018" />
    </g>
    <defs>
      <clipPath id={clipId}>
        <path fill="#fff" d="M.5.583h375v169.052H.5z" />
      </clipPath>
    </defs>
  </svg>
);

// Sports slide — radiating lines / stadium spotlight pattern
const SportsBgSvg = ({ clipId }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 376 170" preserveAspectRatio="xMidYMid slice">
    <g fill="currentColor" clipPath={`url(#${clipId})`}>
      <path d="M188 170 L50 0 L90 0 Z" />
      <path d="M188 170 L130 0 L160 0 Z" />
      <path d="M188 170 L200 0 L230 0 Z" />
      <path d="M188 170 L270 0 L310 0 Z" />
      <path d="M188 170 L340 0 L376 0 Z" />
      <path d="M188 170 L0 0 L20 0 Z" />
      <path d="M0 120 Q188 80 376 120 L376 130 Q188 90 0 130 Z" opacity="0.6" />
      <path d="M0 145 Q188 105 376 145 L376 155 Q188 115 0 155 Z" opacity="0.4" />
    </g>
    <defs>
      <clipPath id={clipId}>
        <path fill="#fff" d="M.5.583h375v169.052H.5z" />
      </clipPath>
    </defs>
  </svg>
);

// Casino slide — diamond / chip grid pattern
const CasinoBgSvg = ({ clipId }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 376 170" preserveAspectRatio="xMidYMid slice">
    <g fill="currentColor" clipPath={`url(#${clipId})`}>
      <path d="M60 30 L80 50 L60 70 L40 50 Z" />
      <path d="M150 20 L170 40 L150 60 L130 40 Z" />
      <path d="M240 35 L260 55 L240 75 L220 55 Z" />
      <path d="M330 25 L350 45 L330 65 L310 45 Z" />
      <path d="M100 90 L120 110 L100 130 L80 110 Z" />
      <path d="M200 95 L220 115 L200 135 L180 115 Z" />
      <path d="M300 100 L320 120 L300 140 L280 120 Z" />
      <path d="M30 120 L50 140 L30 160 L10 140 Z" />
      <path d="M0 0 Q100 40 0 80 Z" opacity="0.5" />
      <path d="M376 0 Q276 40 376 80 Z" opacity="0.5" />
      <path d="M0 90 Q100 130 0 170 Z" opacity="0.5" />
      <path d="M376 90 Q276 130 376 170 Z" opacity="0.5" />
    </g>
    <defs>
      <clipPath id={clipId}>
        <path fill="#fff" d="M.5.583h375v169.052H.5z" />
      </clipPath>
    </defs>
  </svg>
);

const WelcomeBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const logged =
      storage.getKey('logged') === true ||
      localStorage.getItem('logged') === 'true';
    setIsLoggedIn(logged);
  }, []);

  const handleExplore = () => {
    if (!isLoggedIn) {
      navigate('/login?modalType=auth-signup');
    } else {
      const el = document.getElementById('home-content');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDeposit = () => {
    navigate('/wallet?mode=deposit');
  };

  const handleClick = isLoggedIn ? handleDeposit : handleExplore;
  const btnLabel = isLoggedIn ? t('home.depositToUnlock') : t('home.explorePlatform');

  // All 3 slides
  const SLIDES = [
    {
      bgType: 'welcome',
      amount: '$2500',
      title: t('home.welcomePackage'),
      subtitle: 'equivalent to est. ₹233.12K',
      showCoins: true,
      showButton: true,
      tags: null,
    },
    // {
    //   bgType: 'image',
    //   image: '/assets/images/banner/ipl-banner.webp',
    //   link: '/sports',
    // },
    // {
    //   bgType: 'image',
    //   image: '/assets/images/banner/fifa.webp',
    //   link: '/sports',
    // },
    // {
    //   bgType: 'image',
    //   image: '/assets/images/banner/live-casino.webp',
    //   link: '/casino/live',
    // },
  ];

  const TOTAL_SLIDES = SLIDES.length;
  const [activeSlide, setActiveSlide] = useState(0);
  const timerRef = useRef(null);

  const goToSlide = useCallback((idx) => {
    setActiveSlide(idx);
  }, []);

  // Auto-advance every 5 seconds
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % TOTAL_SLIDES);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [TOTAL_SLIDES]);

  const handleDotClick = useCallback((idx) => {
    clearInterval(timerRef.current);
    goToSlide(idx);
    timerRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % TOTAL_SLIDES);
    }, 5000);
  }, [TOTAL_SLIDES, goToSlide]);

  const renderBgSvg = (bgType, idx) => {
    const clipId = `wb_clip_${idx}`;
    if (bgType === 'sports') return <SportsBgSvg clipId={clipId} />;
    if (bgType === 'casino') return <CasinoBgSvg clipId={clipId} />;
    return <WelcomeBgSvg clipId={clipId} />;
  };

  return (
    <section aria-label="Welcome to CRYPTMET">
      <div className={styles.bannerLayout}>

        {/* ===== LEFT PANEL — Slider ===== */}
        <div className={styles.sliderOuter}>
          <div className={styles.sliderTrack} style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
            {SLIDES.map((slide, idx) => (
              <div key={idx} className={styles.sliderSlide}>
                {slide.bgType === 'image' ? (
                  <div
                    className={styles.cardOuter}
                    onClick={() => slide.link && navigate(slide.link)}
                    style={{ cursor: slide.link ? 'pointer' : 'default' }}
                  >
                    <img
                      src={slide.image}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                    />
                  </div>
                ) : (
                  <div className={styles.cardOuter}>
                    <div className={styles.cardInner}>
                      <div className={styles.cardTopGradient} aria-hidden="true" />
                      <div className={styles.svgBg} aria-hidden="true">
                        {renderBgSvg(slide.bgType, idx)}
                      </div>
                      <div className={styles.cardContent}>
                        <div className={styles.heroContent}>
                          <span className={styles.heroAmount}>{slide.amount}</span>
                          <span className={styles.heroTitle}>{slide.title}</span>
                          <span className={styles.heroSubtitle}>{slide.subtitle}</span>
                        </div>

                        {slide.showButton ? (
                          <div className={styles.sliderSection}>
                            {slide.showCoins && (
                              <>
                                <CoinSlider reverse={false} />
                                <CoinSlider reverse={true} />
                              </>
                            )}
                            <div className={styles.ctaWrap}>
                              <div className={styles.pingContainer} aria-hidden="true">
                                <div className={styles.pingRing} />
                              </div>
                              <div className={styles.ctaBtnWrap}>
                                <div className={styles.ctaBtnGlow} aria-hidden="true" />
                                <button className={styles.ctaBtn} onClick={handleClick} type="button">
                                  <span className={styles.ctaBtnInnerGlow} aria-hidden="true" />
                                  <span className={styles.ctaBtnTexture} aria-hidden="true" />
                                  <span className={styles.ctaBtnLabel}>{btnLabel}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.tagsSection}>
                            {slide.tags?.map((tag) => (
                              <span key={tag} className={styles.tag}>{tag}</span>
                            ))}
                          </div>
                        )}

                        {slide.bgType === 'welcome' && (
                          <div className={styles.paymentRow}>
                            {PAYMENT_METHODS.map((method) => (
                              <img key={method.name} src={method.icon} alt={method.name} className={styles.paymentIcon} loading="lazy" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

        
        </div>

        {/* ===== RIGHT PANEL (Desktop only) ===== */}
        {!isMobile && <WelcomeBannerRightPanel />}
      </div>

{/* <div className={styles.bottomAccent} aria-hidden="true" /> */}
    </section>
  );
};

export default WelcomeBanner;
